/* eslint-enable */
import React, { useEffect, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import i18n from '../common/config/i18n';
import {
  RequestStatusBackgroundColorMapper,
  RequestStatusMarkerColorMapper,
  formatDate,
} from '../common/utils/utils';
import StatusWithMarker from './StatusWithMarker';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import { OrderDirection } from '../common/enums/order-direction.enum';
import { useErrorToast, useSuccessToast } from '../hooks/useToast';
import DataTableFilters from './DataTableFilters';
import Card from '../layouts/CardLayout';
import LoadingContent from './LoadingContent';
import { SelectItem } from './Select';
import { IRequest, IRequestDTO } from '../common/interfaces/request.interface';
import CellLayout from './CellLayout';
import Chip from './Chip';
import { RequestTableBasicProps } from './RequestTableWithQueryParams';
import { RequestStatusEnum } from '../common/enums/request-status.enum';
import RequestStatusSelect from './RequestStatusSelect';
import {
  useAddRequestMutation,
  useUpdateRequestMutation,
  useRequestsFilteredByYearAndMonthQuery,
  useRequestsQuery,
  useUpdateRequestStatusMutation,
} from '../services/requests/requests.service';
import PageHeader from './PageHeader';
import PageLayout from '../layouts/PageLayout';
import RequestModal from './RequestModal';
import Button from './Button';
import Popover from './Popover';
import { useTranslation } from 'react-i18next';
import RequesYearMonthPickertModal from './RequestYearMonthPicker';
import { useNavigate, useParams } from 'react-router-dom';
// import { useEntriesQuery } from '../services/entries/entries.service';
import {
  BuildEntriesDetailsConfig,
  BuildPDFConfig,
} from '../common/helpers/certificate-content-generator';
import { dateWithoutTimezone } from '../common/helpers/dates';
import { useUserProfile } from '../contexts/user/UserContext';
import { useClientQuery } from '../services/clients/client.service';
import * as Sentry from '@sentry/react';
import { getPDFGenerator } from '../common/helpers/pdf-generator';
import { useEntries } from '../hooks/useEntries';

const RequestTableHeader: TableColumn<IRequest>[] = [
  {
    id: 'id',
    name: 'ID',
    sortable: true,
    grow: 0.15,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_id__${row.id}`} className="cell-layout">
          {row.id}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'name',
    name: 'Nume',
    grow: 2,
    sortable: true,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_name__${row.id}`} className="cell-layout">
          {row.name}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'cnp',
    name: 'CNP',
    sortable: true,
    grow: 1,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_cnp__${row.id}`} className="cell-layout">
          {row.cnp}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
    grow: 2,
    cell: (row: IRequest) => (
      <CellLayout>
        <Chip bgColor={RequestStatusBackgroundColorMapper[row.status]}>
          <StatusWithMarker
            markerColour={RequestStatusMarkerColorMapper[row.status]}
            textColour={RequestStatusBackgroundColorMapper[row.status]}
            id={`request_status__${row.id}`}
          >
            {i18n.t(`requests:status.${row.status}`)}
          </StatusWithMarker>
        </Chip>
      </CellLayout>
    ),
  },
  {
    id: 'progress',
    name: 'Progres',
    sortable: false,
    grow: 1,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_reg_no__${row.id}`} className="cell-layout">
          {row.fulfilled_months} / {row.months_total}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'start_date',
    name: 'Data inceput',
    sortable: true,
    grow: 1,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_start_date__${row.id}`} className="cell-layout">
          {formatDate(row.start_date)}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'end_date',
    name: 'Data sfarsit',
    sortable: true,
    grow: 1,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_end_date__${row.id}`} className="cell-layout">
          {formatDate(row.end_date)}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'details',
    name: 'Detalii',
    sortable: true,
    grow: 1,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_details__${row.id}`} className="cell-layout">
          {row.details}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'alias',
    name: 'Nume secundar',
    grow: 1,
    sortable: true,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_alias__${row.id}`} className="cell-layout">
          {row.alias}
        </small>
      </CellLayout>
    ),
  },
];

const RequestTableFilteredHeader: TableColumn<IRequest>[] = [
  {
    id: 'id',
    name: 'ID',
    sortable: true,
    grow: 0.15,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_id__${row.id}`} className="cell-layout">
          {row.id}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'name',
    name: 'Nume',
    grow: 1,
    sortable: true,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_name__${row.id}`} className="cell-layout">
          {row.name}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'cnp',
    name: 'CNP',
    sortable: true,
    grow: 1,
    cell: (row: IRequest) => (
      <CellLayout>
        <small id={`request_cnp__${row.id}`} className="cell-layout">
          {row.cnp}
        </small>
      </CellLayout>
    ),
  },
];

type RequestTableType<T extends object> = React.FC<RequestTableBasicProps> & {
  DataTableComponent: React.FC<DataTableProps<T>>;
};

const RequestTable: RequestTableType<IRequest> = ({ query, setQuery }: RequestTableBasicProps) => {
  const { clientId, companyId } = useParams();
  const { user } = useUserProfile();

  const [isAddRequestModalOpen, setIsAddRequestModalOpen] = useState(false);
  const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
  const [selectedEditRequest, setSelectedEditRequest] = useState<IRequest | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<IRequest | null>(null);
  const [isSelectedRequestModalOpen, setIsSelectedRequestModalOpen] = useState(false);
  const [selectedCertificateRequest, setSelectedCertificateRequest] = useState<IRequest | null>(
    null,
  );
  const [selectedAnnexRequest, setSelectedAnnexRequest] = useState<IRequest | null>(null);
  const { t } = useTranslation();

  const currentCompanyId = useMemo(() => {
    if (companyId) {
      return companyId;
    } else if (user?.company_id) {
      return user.company_id;
    } else {
      return '';
    }
  }, [companyId, user]);

  const [status, setStatus] = useState<SelectItem<string>>();
  const {
    data: requests,
    isFetching: isRequestsLoading,
    error: requestsError,
    refetch,
  } = query.year
    ? useRequestsFilteredByYearAndMonthQuery(
        query?.year,
        query?.month,
        query?.limit as number,
        query?.page as number,
        query.orderBy,
        query.orderDirection as OrderDirection,
      )
    : useRequestsQuery(
        currentCompanyId as string,
        clientId as string,
        query?.limit as number,
        query?.page as number,
        query.search,
        query.orderBy,
        query.orderDirection as OrderDirection,
        query.status,
      );

  const { data: client, isFetching: isClientLoading } = useClientQuery(clientId);

  const { ensureData, entriesError } = useEntries(
    selectedCertificateRequest?.id || selectedAnnexRequest?.id,
  );

  // const { data: entries, error: entriesError } = useEntriesQuery(selectedCertificateRequest?.id);

  useEffect(() => {
    if (entriesError) {
      useErrorToast('Eroare la preluarea datelor legate de intrari');
    }
  }, [entriesError]);

  const { mutateAsync: addRequest } = useAddRequestMutation();
  const { mutateAsync: updateRequest } = useUpdateRequestMutation();
  const { mutateAsync: updateRequestStatus } = useUpdateRequestStatusMutation();
  const navigate = useNavigate();

  useEffect(() => {
    if (requestsError) useErrorToast('Eroare la preluarea datelor.');
  }, [requestsError]);

  const buildRequestActionColumn = (): TableColumn<IRequest> => {
    const openRequestMenuItems = [
      {
        label: 'Editeaza',
        onClick: handleOnEdit,
        itemId: 'request_open__kebab_button',
      },
      {
        label: 'Genereaza adeverinta',
        onClick: handleOnGenerateCertificate,
        itemId: 'request_open__kebab_button_certificate',
      },
      {
        label: 'Genereaza anexa',
        onClick: handleOnGenerateAnnex,
        itemId: 'request_open__kebab_button_annex',
      },
      {
        label: 'Marcheaza ca finalizata',
        onClick: handleMarkAsFinished,
        itemId: 'request_open__kebab_button',
      },
      {
        label: 'Marcheaza ca fiind de generat',
        onClick: handleMarkAsNoFinished,
        itemId: 'request_open__kebab_button',
      },
    ];

    return {
      name: '',
      cell: (row: IRequest) => (
        <Popover<IRequest>
          row={row}
          items={openRequestMenuItems}
          popoverId={`request_${row.id}__kebab_menu`}
          className="right-0"
        />
      ),
      width: '60px',
      // allowOverflow: true,
    };
  };

  const onChangeRowsPerPage = (limit: number) => {
    setQuery({ limit, page: 1 }, 'replaceIn');
  };

  const onChangePage = (page: number) => {
    setQuery({ page }, 'replaceIn');
  };

  const onSort = (column: TableColumn<IRequest>, direction: SortOrder) => {
    setQuery(
      {
        orderBy: column.id as string,
        orderDirection:
          direction.toLocaleUpperCase() === OrderDirection.ASC
            ? OrderDirection.ASC
            : OrderDirection.DESC,
      },
      'replaceIn',
    );
  };

  const onSearch = (search: string) => {
    setQuery({ search }, 'replaceIn');
  };

  const onResetFilters = () => {
    setStatus(undefined);
    setQuery({}, 'push');
  };

  const onStatusChange = (statusItem: SelectItem<string>) => {
    setStatus(statusItem);
    setQuery({ status: statusItem.key as RequestStatusEnum }, 'replaceIn');
  };

  const handleRowClick = (row: IRequest) => {
    if (query.year && query.month) {
      const route = companyId
        ? `/companies/${companyId}/clients/${clientId}/requests`
        : `/clients/${clientId}/requests`;

      navigate(`${route}/${row.id}/${query.year}/${query.month}`);
    } else {
      setSelectedRequest(row);
      setIsSelectedRequestModalOpen(true);
    }
  };

  const handleOnEdit = (row: IRequest) => {
    setSelectedEditRequest(row);
    setIsEditRequestModalOpen(true);
  };

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const handleOnAddRequest = (data: any) => {
    const start_date = dateWithoutTimezone(data.start_date);
    const end_date = dateWithoutTimezone(data.end_date);
    addRequest(
      { ...data, start_date, end_date, company_id: currentCompanyId, client_id: clientId },
      {
        onSuccess: () => {
          refetch();
          setIsAddRequestModalOpen(false);
          useSuccessToast('Cererea a fost adaugata cu succes');
        },
        onError: () => {
          useErrorToast('Eroare la adaugarea cererii');
        },
      },
    );
  };

  const handleOnEditRequest = (data: IRequestDTO) => {
    updateRequest(data, {
      onSuccess: () => {
        setSelectedEditRequest(null);
        setIsEditRequestModalOpen(false);
        refetch();
        useSuccessToast('Cererea a fost editata cu succes');
      },
      onError: () => {
        useErrorToast('Eroare la editarea cererii');
      },
      onSettled: () => setIsEditRequestModalOpen(false),
    });
  };

  const handleOnNavigate = (data: {
    year: SelectItem<string> | undefined;
    month: SelectItem<string> | undefined;
  }) => {
    if (selectedRequest && data.year && data.month) {
      navigate(`${selectedRequest.id}/${data.year.value}/${data.month.value}`);
    }
  };

  const handleOnGenerateCertificate = async (row: IRequest) => {
    setSelectedCertificateRequest(row);
  };

  const handleOnGenerateAnnex = async (row: IRequest) => {
    setSelectedAnnexRequest(row);
  };

  useEffect(() => {
    if (selectedAnnexRequest) {
      generateAnnex();
    }
  }, [selectedAnnexRequest]);

  useEffect(() => {
    if (selectedCertificateRequest) {
      generateCertificate();
    }
  }, [selectedCertificateRequest]);

  const generateAnnex = async () => {
    try {
      console.log('waiting for entries');
      const entries = await ensureData();
      console.log('waited', entries?.length);

      if (entries && selectedAnnexRequest && !client?.annex) {
        useErrorToast('Clientul nu are configurat un model de anexa');
        return;
      }

      if (
        entries &&
        selectedAnnexRequest &&
        entries[0]?.request_id === selectedAnnexRequest?.id &&
        client?.annex
      ) {
        try {
          console.log('generating annex');
          const content = BuildPDFConfig(
            client?.annex.config,
            selectedAnnexRequest,
            entries,
            client,
            client.annex.clients.companies.name,
          );
          const pdfGenerator = getPDFGenerator(content);
          pdfGenerator.download(
            `Anexa-${selectedAnnexRequest.name}-${selectedAnnexRequest.cnp}.pdf`,
          );
          setSelectedAnnexRequest(null);
        } catch (err) {
          useErrorToast('Eroare la generarea adeverintei');
          Sentry.captureException(err);
        }
      }
    } catch (err) {
      useErrorToast('Eroare la generarea adeverintei');
      Sentry.captureException(err);
    }
  };

  const generateCertificate = async () => {
    try {
      const entries = await ensureData();

      if (entries && selectedCertificateRequest && !client?.certificate) {
        useErrorToast('Clientul nu are configurat un model de adeverinta');
        return;
      }

      if (
        entries &&
        selectedCertificateRequest &&
        entries[0]?.request_id === selectedCertificateRequest?.id &&
        client?.certificate
      ) {
        try {
          const content = BuildPDFConfig(
            client?.certificate.config,
            selectedCertificateRequest,
            entries,
            client,
            client.certificate.clients.companies.name,
          );
          const entriesWithDetails = BuildEntriesDetailsConfig(
            client.certificate.config,
            selectedCertificateRequest,
            entries,
          );
          const pdfGenerator = getPDFGenerator(content);
          pdfGenerator.download(
            `Adeverinta-${selectedCertificateRequest.name}-${selectedCertificateRequest.cnp}.pdf`,
          );
          const pdfGeneratorObservations = getPDFGenerator(entriesWithDetails);
          pdfGeneratorObservations.download(
            `Observatii-${selectedCertificateRequest.name}-${selectedCertificateRequest.cnp}.pdf`,
          );
          setSelectedCertificateRequest(null);
        } catch (err) {
          useErrorToast('Eroare la generarea adeverintei');
          Sentry.captureException(err);
        }
      }
    } catch (err) {
      useErrorToast('Eroare la generarea adeverintei');
      Sentry.captureException(err);
    }
  };

  const handleMarkAsFinished = (row: IRequest) => {
    updateRequestStatus(
      { id: row.id, status: RequestStatusEnum.FINISHED },
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          useErrorToast('Eroare la marcarea cererii ca finalizata');
        },
      },
    );
  };

  const handleMarkAsNoFinished = (row: IRequest) => {
    updateRequestStatus(
      { id: row.id, status: RequestStatusEnum.TO_DELIVER },
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          useErrorToast('Eroare la marcarea cererii ca fiind de generat');
        },
      },
    );
  };

  return (
    <PageLayout>
      {!query.year && (
        <div className="flex gap-4 justify-between w-full">
          <PageHeader title={`Cereri - ${isClientLoading ? '...' : client?.name}`} />
          <Button
            id="project_add__button"
            className="btn-primary"
            onClick={setIsAddRequestModalOpen.bind(null, true)}
            label={'Adauga cerere'}
          />
        </div>
      )}
      <Card>
        {!query.year && (
          <DataTableFilters
            onSearch={onSearch}
            onResetFilters={onResetFilters}
            searchValue={query.search}
          >
            <RequestStatusSelect
              id="request_status_filter__select"
              onChange={onStatusChange}
              selected={status}
              className="max-h-52"
            />
          </DataTableFilters>
        )}
        <RequestTable.DataTableComponent
          columns={
            [
              ...(query.year ? RequestTableFilteredHeader : RequestTableHeader),
              ...(query.year ? [] : [buildRequestActionColumn()]),
            ] as TableColumn<IRequest>[]
          }
          pagination
          onSort={onSort}
          onRowClicked={handleRowClick}
          data={requests?.items}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          pointerOnHover={true}
          paginationDefaultPage={query.page}
          paginationPerPage={query.limit}
          paginationTotalRows={requests?.meta.totalItems}
          defaultSortAsc={false}
          loading={isRequestsLoading}
        />
        {isRequestsLoading && <LoadingContent />}
      </Card>
      {isAddRequestModalOpen && (
        <RequestModal
          onClose={setIsAddRequestModalOpen.bind(null, false)}
          onSubmit={handleOnAddRequest}
          title={t('request_modal:form_name')}
          buttonLabel={t('request_modal:form_button')}
        />
      )}
      {isEditRequestModalOpen && (
        <RequestModal
          onClose={setIsEditRequestModalOpen.bind(null, false)}
          onSubmit={handleOnEditRequest}
          title={t('request_modal:form_name_edit')}
          buttonLabel={t('request_modal:form_edit_button')}
          request={selectedEditRequest}
        />
      )}
      {isSelectedRequestModalOpen && (
        <RequesYearMonthPickertModal
          onClose={setIsSelectedRequestModalOpen.bind(null, false)}
          onSubmit={handleOnNavigate}
          title={t('request_navigation_modal:title')}
          buttonLabel={t('request_navigation_modal:button')}
          request={selectedRequest}
        />
      )}
    </PageLayout>
  );
};

RequestTable.DataTableComponent = DataTableComponent;

export default RequestTable;
